export interface WikiPageMeta {
  slug: string;
  title: string;
  category: 'Getting Started' | 'User Guides & Features' | 'Development & Reference';
  description: string;
}

export interface ReleaseAsset {
  name: string;
  downloadUrl: string;
  size: string;
  type: 'deb' | 'rpm' | 'arch' | 'appimage' | 'windows' | 'binary' | 'whl' | 'tar' | 'checksum' | 'other';
}

export interface ReleaseInfo {
  version: string;
  name: string;
  publishedAt: string;
  htmlUrl: string;
  body: string;
  assets: ReleaseAsset[];
  tarballUrl: string;
  zipballUrl: string;
  isPrerelease?: boolean;
}

export interface ReleasesSummary {
  stable: ReleaseInfo;
  prerelease: ReleaseInfo | null;
}

export const PROJECT_CONFIG = {
  name: 'Mastui',
  tagline: 'Retro-modern Mastodon client for your terminal',
  subheading: 'Blazing-fast TUI built with Python & Textual. Multi-column timelines, ANSI/Sixel/TGP image rendering, persistent SQLite cache, and an elegant keyboard-driven workflow.',
  domain: 'mastui.app',
  githubRepo: 'kimusan/mastui',
  githubUrl: 'https://github.com/kimusan/mastui',
  githubIssuesUrl: 'https://github.com/kimusan/mastui/issues',
  githubNewIssueUrl: 'https://github.com/kimusan/mastui/issues/new/choose',
  githubNewBugUrl: 'https://github.com/kimusan/mastui/issues/new?template=bug_report.md',
  githubNewFeatureUrl: 'https://github.com/kimusan/mastui/issues/new?template=feature_request.md',
  githubDiscussionsUrl: 'https://github.com/kimusan/mastui/discussions',
  githubWikiBaseUrl: 'https://raw.githubusercontent.com/wiki/kimusan/mastui',
  githubWikiWebUrl: 'https://github.com/kimusan/mastui/wiki',
  pypiUrl: 'https://pypi.org/project/mastui/',
  pypiApiUrl: 'https://pypi.org/pypi/mastui/json',
  mastodonUrl: 'https://mastodon.social/@Mastui',
  maintainerName: 'Kim Schulz',
  maintainerUrl: 'https://schulz.dk',
  maintainerGithub: 'https://github.com/kimusan',
  license: 'MIT License',
  licenseUrl: 'https://github.com/kimusan/mastui/blob/main/LICENSE',
};

export const FALLBACK_RELEASE: ReleaseInfo = {
  version: 'v1.14.2',
  name: 'v1.14.2',
  publishedAt: '2026-09-02',
  htmlUrl: 'https://github.com/kimusan/mastui/releases/tag/v1.14.2',
  body: `### Highlights & Bugfixes in v1.14.2\n\n- **Critical Fix (OAuth Tokens)**: Resolved token renewal race condition during parallel background timeline polls (v1.14.1).\n- **Critical Fix (Sixel Images)**: Prevented terminal dimension calculation overflow and layout loops when resizing under 80 columns (v1.14.1).\n- **Critical Fix (SQLite Persistence)**: Implemented non-blocking connection pooling and WAL mode with busy timeouts to avoid UI freezes on laptop sleep/resume (v1.14.2).\n- **Resilient Keymaps**: Enhanced \`keymap.json\` parser to tolerate trailing commas with interactive error reporting (v1.14.2).\n- **Streaming & Notifications**: Real-time WebSocket backpressure management and synchronized notification badges.`,
  tarballUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2.tar.gz',
  zipballUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-windows-x86_64.zip',
  isPrerelease: false,
  assets: [
    {
      name: 'mastui (Standalone Linux Binary)',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui',
      size: '41.8 MB',
      type: 'binary'
    },
    {
      name: 'mastui_1.14.2_amd64.deb',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui_1.14.2_amd64.deb',
      size: '42.5 MB',
      type: 'deb'
    },
    {
      name: 'mastui-1.14.2-1.x86_64.rpm',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-1.x86_64.rpm',
      size: '42.6 MB',
      type: 'rpm'
    },
    {
      name: 'mastui-1.14.2-1-x86_64.pkg.tar.zst',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-1-x86_64.pkg.tar.zst',
      size: '42.8 MB',
      type: 'arch'
    },
    {
      name: 'mastui-1.14.2-x86_64.AppImage',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-x86_64.AppImage',
      size: '43.6 MB',
      type: 'appimage'
    },
    {
      name: 'mastui.exe (Windows Standalone)',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui.exe',
      size: '28.7 MB',
      type: 'windows'
    },
    {
      name: 'mastui-1.14.2-windows-x86_64.zip',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-windows-x86_64.zip',
      size: '28.5 MB',
      type: 'windows'
    },
    {
      name: 'mastui-1.14.2-py3-none-any.whl',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2-py3-none-any.whl',
      size: '96 KB',
      type: 'whl'
    },
    {
      name: 'mastui-1.14.2.tar.gz',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/mastui-1.14.2.tar.gz',
      size: '79 KB',
      type: 'tar'
    },
    {
      name: 'SHA256SUMS.txt',
      downloadUrl: 'https://github.com/kimusan/mastui/releases/download/v1.14.2/SHA256SUMS.txt',
      size: '813 B',
      type: 'checksum'
    }
  ]
};

export const FALLBACK_PRERELEASE: ReleaseInfo | null = null;

export const WIKI_PAGES: WikiPageMeta[] = [
  {
    slug: 'Home',
    title: 'Overview & Introduction',
    category: 'Getting Started',
    description: 'Introduction to Mastui, key features, requirements, and quick start guide.'
  },
  {
    slug: 'Installation',
    title: 'Installation Guide',
    category: 'Getting Started',
    description: 'Detailed instructions for pipx, .deb, .rpm, Arch Linux, AppImage, Windows, and Termux.'
  },
  {
    slug: 'Keybindings',
    title: 'Keyboard Navigation & Shortcuts',
    category: 'User Guides & Features',
    description: 'Complete list of keyboard shortcuts for timelines, composer, navigation, and custom keymap.json.'
  },
  {
    slug: 'Media-And-Images',
    title: 'Image Protocols (Sixel / TGP / ANSI)',
    category: 'User Guides & Features',
    description: 'High-fidelity image rendering protocols, terminal compatibility, and image cache.'
  },
  {
    slug: 'Filters-And-Options',
    title: 'Server-Side Filters & Options',
    category: 'User Guides & Features',
    description: 'Manage Mastodon keyword filters, configure timelines, themes, and language selection.'
  },
  {
    slug: 'Multi-Profile',
    title: 'Multi-Profile Account Setup',
    category: 'User Guides & Features',
    description: 'Manage multiple Mastodon accounts with independent credentials, configs, and caches.'
  },
  {
    slug: 'Changelog',
    title: 'Changelog & Version History',
    category: 'Development & Reference',
    description: 'Historical changelog and detailed release notes across all versions.'
  },
  {
    slug: 'Roadmap',
    title: 'Project Roadmap',
    category: 'Development & Reference',
    description: 'Upcoming features, milestones, and medium-term development plans.'
  },
  {
    slug: 'Contributing',
    title: 'Contributing & Development',
    category: 'Development & Reference',
    description: 'Developer setup with Poetry, code standards, packaging scripts, and PR guidelines.'
  },
  {
    slug: 'Security',
    title: 'Security Policy',
    category: 'Development & Reference',
    description: 'Security architecture, credential sandboxing, link sanitization, and reporting.'
  }
];

export const INSTALL_SNIPPETS = {
  pipx: `# 1) Install pipx (if needed)
python3 -m pip install --user pipx
python3 -m pipx ensurepath

# 2) Install Mastui
pipx install mastui

# 3) Run Mastui
mastui`,

  deb: `# Download and install the latest .deb package on Debian/Ubuntu
wget https://github.com/kimusan/mastui/releases/download/v1.12.0/mastui_1.12.0_amd64.deb
sudo apt install ./mastui_1.12.0_amd64.deb

# Run Mastui
mastui`,

  rpm: `# Download and install the latest .rpm package on Fedora / RHEL
wget https://github.com/kimusan/mastui/releases/download/v1.12.0/mastui-1.12.0-1.x86_64.rpm
sudo dnf install ./mastui-1.12.0-1.x86_64.rpm

# Run Mastui
mastui`,

  arch: `# Download and install via pacman on Arch Linux
wget https://github.com/kimusan/mastui/releases/download/v1.12.0/mastui-1.12.0-1-x86_64.pkg.tar.zst
sudo pacman -U mastui-1.12.0-1-x86_64.pkg.tar.zst

# Run Mastui
mastui`,

  appimage: `# Download and run standalone Linux AppImage
wget https://github.com/kimusan/mastui/releases/download/v1.12.0/mastui-1.12.0-x86_64.AppImage
chmod +x mastui-1.12.0-x86_64.AppImage
./mastui-1.12.0-x86_64.AppImage`,

  windows: `# In PowerShell / Windows Terminal:
# Download mastui-1.12.0-windows-x86_64.zip from GitHub Releases,
# extract and run:
.\\mastui.exe`,

  source: `# Clone and run with Poetry
git clone https://github.com/kimusan/mastui.git
cd mastui
poetry install
poetry run mastui`,

  termux: `# On Android using Termux:
pkg update && pkg upgrade
pkg install python python-pip
pip install mastui
mastui`
};

export const SHORTCUTS_DATA = [
  { key: 'j / ↓', action: 'Move down to next post', category: 'Timeline' },
  { key: 'k / ↑', action: 'Move up to previous post', category: 'Timeline' },
  { key: 'g', action: 'Jump to top of timeline', category: 'Timeline' },
  { key: 'G', action: 'Jump to bottom of timeline', category: 'Timeline' },
  { key: 'r', action: 'Refresh current timeline', category: 'Timeline' },
  { key: 'Enter', action: 'Open thread / conversation view', category: 'Timeline' },
  { key: 'b', action: 'Boost / Retoot selected post', category: 'Timeline' },
  { key: 'f / l', action: 'Favorite / Like selected post', category: 'Timeline' },
  { key: 'u', action: 'Extract URLs and media links modal', category: 'Timeline' },
  { key: 'c', action: 'Compose new toot', category: 'Composer' },
  { key: 'Tab', action: 'Autocomplete @user or #hashtag / Next field', category: 'Composer' },
  { key: 'Ctrl+w', action: 'Toggle Content Warning (CW)', category: 'Composer' },
  { key: 'Ctrl+p', action: 'Toggle Poll builder', category: 'Composer' },
  { key: 'Ctrl+s', action: 'Publish / Send toot', category: 'Composer' },
  { key: 'h / l', action: 'Previous / Next column', category: 'Navigation' },
  { key: 's', action: 'Search users, tags, and posts', category: 'Navigation' },
  { key: 'p', action: 'Profile & account switcher', category: 'Navigation' },
  { key: 'o', action: 'Options, filters, and settings', category: 'Navigation' },
  { key: '?', action: 'Show help & shortcuts overlay', category: 'Global' },
  { key: 'F12', action: 'Toggle debug log viewer (--debug)', category: 'Global' },
  { key: 'q', action: 'Quit / Close dialog', category: 'Global' },
];

export const GALLERY_ITEMS = [
  {
    id: 'main-view',
    title: 'Multi-Column Timelines',
    description: 'Simultaneously monitor Home, Notifications, Mentions, Local, and Federated timelines with smooth live updates and independent column scrolling.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-main-view.png',
    badge: 'Core UI'
  },
  {
    id: 'thread-view',
    title: 'Thread & Conversation Inspector',
    description: 'Inspect deeply nested conversations, replies, context trees, and original posts without losing your timeline position.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-thread-view.png',
    badge: 'Conversations'
  },
  {
    id: 'composer-poll',
    title: 'Rich Composer & Poll Builder',
    description: 'Write toots with autocomplete for @mentions and #hashtags, content warnings (CW), custom visibility, language tagging, and interactive poll creation.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-compose-poll.png',
    badge: 'Publishing'
  },
  {
    id: 'image-rendering',
    title: 'Sixel, ANSI & TGP Image Previews',
    description: 'View photos, artwork, and media attachments directly inside your terminal with automatic graphics protocol detection and local disk caching.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-image-support.png',
    badge: 'Media'
  },
  {
    id: 'profile-view',
    title: 'Account Profiles & Relationships',
    description: 'Explore full user bios, follower counts, instance details, avatar rendering, and execute follow, mute, and block actions directly.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-profile-view.png',
    badge: 'Social'
  },
  {
    id: 'retro-theme',
    title: 'Phosphor Green CRT Theme',
    description: 'Experience an authentic retro computing aesthetic with high-contrast phosphor green on deep black, paired with modern UI speed.',
    image: 'https://raw.githubusercontent.com/kimusan/mastui/refs/heads/main/assets/screenshots/mastui-retro-green-on-black-theme.png',
    badge: 'Themes'
  }
];
